/**
 * Settings → Jira Integration page (136 D5).
 *
 * Lists every Jira credential configured for the active org and
 * surfaces admin actions (Add / Rotate / Rename / Delete). Multi-key
 * is first-class: the page treats zero, one, and many credentials
 * identically modulo the empty state.
 *
 * Status pill per row is driven by the latest jira_discovery_run
 * (task 137 §D7): Syncing… / Connected / Auth failed / Upstream error
 * (retrying) / Pending first sync. The "Sync now" button per row
 * dispatches a manual discovery; the row is expandable to show the
 * last run's projects/users/links counts.
 *
 * Mockup mode ships a two-credential fixture (one succeeded, one
 * auth_failed) so the page demonstrates every status the live
 * backend can produce.
 *
 * Admin-only: non-admin members see an access-denied notice. The
 * backend enforces admin RBAC on every mutation route anyway; this
 * is UX, not an authorization boundary.
 */

import { useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/composites/PageHeader';
import { SectionCard } from '@/components/composites/SectionCard';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { Badge } from '@/components/primitives/Badge';
import { Button } from '@/components/primitives/Button';
import { EmptyState } from '@/components/primitives/EmptyState';
import { TextInput } from '@/components/primitives/TextInput';
import { useAppContext } from '@/context/AppContext';
import { formatDateTimeShort, formatRelativeShort } from '@/lib/dates';
import {
  useCreateJiraCredential,
  useDeleteJiraCredential,
  useJiraCredentialDiscoveryRun,
  useJiraCredentialWorklogStatus,
  useJiraCredentials,
  useRenameJiraCredential,
  useRotateJiraCredential,
  useTriggerJiraSyncNow,
  type JiraCredential,
  type JiraDiscoveryRun,
  type JiraWorklogStatus,
} from '@/hooks/useJiraCredentials';
import styles from './SettingsJiraPage.module.css';

const SITE_URL_HINT = 'e.g. https://acme.atlassian.net — no trailing slash, no path.';

const SITE_URL_REGEX = /^https:\/\/[a-z0-9][a-z0-9\-.]*$/;

export function SettingsJiraPage() {
  const { currentUser, mockupMode } = useAppContext();
  const { credentials, isLoading, isError, error } = useJiraCredentials();

  const [addOpen, setAddOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState<JiraCredential | null>(null);
  const [renameTarget, setRenameTarget] = useState<JiraCredential | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JiraCredential | null>(null);

  if (currentUser.role !== 'admin') {
    return (
      <>
        <PageHeader
          title="Jira integration"
          subtitle="Connect Kanjoy to one or more Atlassian sites."
        />
        <SectionCard title="Access">
          <p className={styles.warning}>
            Only admins can manage Jira connections. Ask an admin in your organization to add a
            connection.
          </p>
        </SectionCard>
      </>
    );
  }

  const addButton = (
    <Button
      variant="primary"
      size="md"
      type="button"
      onClick={() => setAddOpen(true)}
      disabled={mockupMode}
      title={mockupMode ? 'Mockup mode — live settings are not editable' : undefined}
    >
      Add connection
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Jira integration"
        subtitle="Kanjoy reads projects, users, and worklogs from each connected Atlassian site. Each connection is one Atlassian site; a firm with multiple sites can add a connection per site."
        actions={addButton}
      />

      <SectionCard title="Connections">
        {isLoading && <p className={styles.loading}>Loading connections…</p>}

        {!isLoading && isError && (
          <p className={styles.error}>
            Failed to load connections: {error?.message ?? 'Unknown error'}
          </p>
        )}

        {!isLoading && !isError && credentials.length === 0 && (
          <EmptyState
            title="No Jira connections yet"
            description="Kanjoy will not fetch any Jira data for this organization until you add a connection."
          />
        )}

        {!isLoading && !isError && credentials.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th aria-label="Expand row" />
                  <th>Label</th>
                  <th>Site URL</th>
                  <th>Status</th>
                  <th>Added</th>
                  <th>Last synced</th>
                  <th className={styles.actionsCol}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {credentials.map((c) => (
                  <CredentialRow
                    key={c.id}
                    credential={c}
                    onRotate={() => setRotateTarget(c)}
                    onRename={() => setRenameTarget(c)}
                    onDelete={() => setDeleteTarget(c)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <AddConnectionModal open={addOpen} onClose={() => setAddOpen(false)} />
      {rotateTarget && (
        <RotateModal credential={rotateTarget} onClose={() => setRotateTarget(null)} />
      )}
      {renameTarget && (
        <RenameModal credential={renameTarget} onClose={() => setRenameTarget(null)} />
      )}
      {deleteTarget && (
        <DeleteModal credential={deleteTarget} onClose={() => setDeleteTarget(null)} />
      )}
    </>
  );
}

// ── Per-credential row (task 137 §D7) ────────────────────────────────────

interface CredentialRowProps {
  credential: JiraCredential;
  onRotate: () => void;
  onRename: () => void;
  onDelete: () => void;
}

function CredentialRow({ credential: c, onRotate, onRename, onDelete }: CredentialRowProps) {
  const { lastRun } = useJiraCredentialDiscoveryRun(c.id);
  const triggerSync = useTriggerJiraSyncNow();
  const [expanded, setExpanded] = useState(false);

  const isInFlight = lastRun?.status === 'running';
  const lastSucceeded = lastRun && lastRun.status === 'succeeded' ? lastRun : null;

  return (
    <>
      <tr>
        <td>
          <button
            type="button"
            className={styles.expandToggle}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '▾' : '▸'}
          </button>
        </td>
        <td>{c.label}</td>
        <td>
          <code className={styles.siteUrl}>{c.siteUrl}</code>
        </td>
        <td className={styles.statusCell}>
          <StatusPill run={lastRun} />
        </td>
        <td>{formatDateTimeShort(c.createdAt)}</td>
        <td>{lastSucceeded?.finishedAt ? formatRelativeShort(lastSucceeded.finishedAt) : '—'}</td>
        <td className={styles.actionsCol}>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={isInFlight || triggerSync.isPending}
            onClick={() => triggerSync.mutate(c.id)}
            title={isInFlight ? 'A sync is already running' : undefined}
          >
            {isInFlight || triggerSync.isPending ? 'Syncing…' : 'Sync now'}
          </Button>
          <Button variant="secondary" size="sm" type="button" onClick={onRotate}>
            Rotate
          </Button>
          <Button variant="secondary" size="sm" type="button" onClick={onRename}>
            Rename
          </Button>
          <Button variant="secondary" size="sm" type="button" onClick={onDelete}>
            Delete
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr className={styles.runDetailRow}>
          <td colSpan={7}>
            <RunDetail run={lastRun} credentialId={c.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function StatusPill({ run }: { run: JiraDiscoveryRun | null }) {
  if (!run) {
    return <Badge variant="general">Pending first sync</Badge>;
  }
  switch (run.status) {
    case 'running':
      return <Badge variant="general">Syncing…</Badge>;
    case 'succeeded':
      return <Badge variant="enabled">Connected</Badge>;
    case 'auth_failed':
      return (
        <span
          className={styles.statusTooltip}
          title={run.errorDetail ?? 'Atlassian rejected the credential'}
        >
          <Badge variant="disabled">Auth failed</Badge>
        </span>
      );
    case 'rate_limited':
    case 'upstream_error':
      return (
        <span className={styles.statusTooltip} title={run.errorDetail ?? 'Upstream issue'}>
          <Badge variant="general">Upstream error (retrying)</Badge>
        </span>
      );
    case 'abandoned':
      return <Badge variant="disabled">Abandoned</Badge>;
  }
}

function RunDetail({ run, credentialId }: { run: JiraDiscoveryRun | null; credentialId: string }) {
  if (!run) {
    return <p className={styles.warning}>No discovery run yet for this connection.</p>;
  }
  return (
    <>
      <dl className={styles.runDetailGrid}>
        <div>
          <dt>Started</dt>
          <dd>{formatDateTimeShort(run.startedAt)}</dd>
        </div>
        <div>
          <dt>Finished</dt>
          <dd>{run.finishedAt ? formatDateTimeShort(run.finishedAt) : 'in flight…'}</dd>
        </div>
        <div>
          <dt>Trigger</dt>
          <dd>{run.trigger}</dd>
        </div>
        <div>
          <dt>Projects seen</dt>
          <dd>{run.projectsSeen}</dd>
        </div>
        <div>
          <dt>Projects archived</dt>
          <dd>{run.projectsArchived}</dd>
        </div>
        <div>
          <dt>Users seen</dt>
          <dd>{run.usersSeen}</dd>
        </div>
        <div>
          <dt>Users deactivated</dt>
          <dd>{run.usersDeactivated}</dd>
        </div>
        <div>
          <dt>Employees linked</dt>
          <dd>{run.linksCreated}</dd>
        </div>
        {run.errorDetail && (
          <div>
            <dt>Error detail</dt>
            <dd>{run.errorDetail}</dd>
          </div>
        )}
      </dl>
      <WorklogDetail credentialId={credentialId} />
    </>
  );
}

// Task 138 §D7 — worklog poller status, rendered in the same
// expanded row as discovery detail. Shows freshness, total worklog
// count, and orphan count.
function WorklogDetail({ credentialId }: { credentialId: string }) {
  const { status, isLoading } = useJiraCredentialWorklogStatus(credentialId);
  return (
    <>
      <h4 className={styles.worklogHeading}>Worklogs</h4>
      {isLoading && <p className={styles.loading}>Loading worklog status…</p>}
      {!isLoading && <WorklogDetailGrid status={status} />}
    </>
  );
}

function WorklogDetailGrid({ status }: { status: JiraWorklogStatus | null }) {
  if (!status || status.totalWorklogs === 0) {
    return (
      <p className={styles.warning}>
        No worklogs synced yet for this connection. The poller runs every 15 minutes.
      </p>
    );
  }
  return (
    <dl className={styles.runDetailGrid}>
      <div>
        <dt>Last synced</dt>
        <dd>{status.lastRefreshedAt ? formatRelativeShort(status.lastRefreshedAt) : '—'}</dd>
      </div>
      <div>
        <dt>Worklogs tracked</dt>
        <dd>{status.totalWorklogs.toLocaleString()}</dd>
      </div>
      <div>
        <dt>Orphans (unlinked)</dt>
        <dd>{status.orphans.toLocaleString()}</dd>
      </div>
    </dl>
  );
}

// ── Add ──────────────────────────────────────────────────────────────────

function AddConnectionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateJiraCredential();
  const [siteUrl, setSiteUrl] = useState('');
  const [label, setLabel] = useState('');
  const [botEmail, setBotEmail] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setSiteUrl('');
    setLabel('');
    setBotEmail('');
    setApiToken('');
    setShowToken(false);
    setErr(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!SITE_URL_REGEX.test(siteUrl)) {
      setErr(
        'Site URL must be of the form https://{lowercase-host} — no trailing slash, no path, no query.',
      );
      return;
    }
    if (label.trim().length === 0) {
      setErr('Label is required.');
      return;
    }
    try {
      await create.mutateAsync({
        siteUrl,
        label: label.trim(),
        botEmail,
        apiToken,
      });
      handleClose();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Unknown error');
    }
  };

  return (
    <SlidePanel
      open={open}
      onClose={handleClose}
      title="Add Jira connection"
      subtitle="Connect Kanjoy to an Atlassian site using a service-account API token."
      width="md"
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <TextInput
          label="Site URL"
          type="text"
          value={siteUrl}
          onChange={(e) => setSiteUrl(e.target.value)}
          placeholder="https://acme.atlassian.net"
          autoComplete="off"
          required
          hint={SITE_URL_HINT}
        />
        <TextInput
          label="Label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          autoComplete="off"
          required
          hint="A short name shown in this list. Doesn't have to match the site URL."
        />
        <TextInput
          label="Bot email"
          type="email"
          value={botEmail}
          onChange={(e) => setBotEmail(e.target.value)}
          autoComplete="off"
          required
          hint="The Atlassian account that owns the API token."
        />
        <div className={styles.tokenRow}>
          <TextInput
            label="API token"
            type={showToken ? 'text' : 'password'}
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            autoComplete="off"
            required
            hint="Stored encrypted at rest (pgcrypto). Never displayed after save."
          />
          <button
            type="button"
            className={styles.toggleTokenBtn}
            onClick={() => setShowToken((v) => !v)}
            aria-label={showToken ? 'Hide token' : 'Show token'}
          >
            {showToken ? 'Hide' : 'Show'}
          </button>
        </div>

        {err && <p className={styles.error}>{err}</p>}

        <div className={styles.actions}>
          <Button variant="secondary" size="md" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Save connection'}
          </Button>
        </div>
      </form>
    </SlidePanel>
  );
}

// ── Rotate ───────────────────────────────────────────────────────────────

function RotateModal({ credential, onClose }: { credential: JiraCredential; onClose: () => void }) {
  const rotate = useRotateJiraCredential();
  const [botEmail, setBotEmail] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      await rotate.mutateAsync({
        credentialId: credential.id,
        botEmail,
        apiToken,
      });
      onClose();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Unknown error');
    }
  };

  return (
    <SlidePanel
      open
      onClose={onClose}
      title="Rotate credential"
      subtitle={`Replace the stored bot email + API token for ${credential.label} (${credential.siteUrl}).`}
      width="md"
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <TextInput
          label="Bot email"
          type="email"
          value={botEmail}
          onChange={(e) => setBotEmail(e.target.value)}
          autoComplete="off"
          required
        />
        <div className={styles.tokenRow}>
          <TextInput
            label="API token"
            type={showToken ? 'text' : 'password'}
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            autoComplete="off"
            required
          />
          <button
            type="button"
            className={styles.toggleTokenBtn}
            onClick={() => setShowToken((v) => !v)}
            aria-label={showToken ? 'Hide token' : 'Show token'}
          >
            {showToken ? 'Hide' : 'Show'}
          </button>
        </div>

        {err && <p className={styles.error}>{err}</p>}

        <div className={styles.actions}>
          <Button variant="secondary" size="md" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" disabled={rotate.isPending}>
            {rotate.isPending ? 'Rotating…' : 'Rotate'}
          </Button>
        </div>
      </form>
    </SlidePanel>
  );
}

// ── Rename ───────────────────────────────────────────────────────────────

function RenameModal({ credential, onClose }: { credential: JiraCredential; onClose: () => void }) {
  const rename = useRenameJiraCredential();
  const [label, setLabel] = useState(credential.label);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (label.trim().length === 0) {
      setErr('Label is required.');
      return;
    }
    try {
      await rename.mutateAsync({ credentialId: credential.id, label: label.trim() });
      onClose();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Unknown error');
    }
  };

  return (
    <SlidePanel
      open
      onClose={onClose}
      title="Rename connection"
      subtitle={`Update the label for ${credential.siteUrl}. Does not rotate the token.`}
      width="sm"
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <TextInput
          label="Label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          autoComplete="off"
          required
        />
        {err && <p className={styles.error}>{err}</p>}
        <div className={styles.actions}>
          <Button variant="secondary" size="md" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" disabled={rename.isPending}>
            {rename.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </SlidePanel>
  );
}

// ── Delete ───────────────────────────────────────────────────────────────

function DeleteModal({ credential, onClose }: { credential: JiraCredential; onClose: () => void }) {
  const del = useDeleteJiraCredential();
  const [err, setErr] = useState<string | null>(null);

  const handleConfirm = async () => {
    setErr(null);
    try {
      await del.mutateAsync(credential.id);
      onClose();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Unknown error');
    }
  };

  return (
    <SlidePanel
      open
      onClose={onClose}
      title="Delete connection"
      subtitle={`${credential.label} (${credential.siteUrl})`}
      width="sm"
    >
      <div className={styles.form}>
        <p>
          Kanjoy will stop fetching any data from this Atlassian site. Once the discovery feature
          ships, any projects, users, and worklogs Kanjoy had mirrored under this connection are
          removed with it. This cannot be undone without adding the connection back and letting
          Kanjoy rediscover the data.
        </p>
        {err && <p className={styles.error}>{err}</p>}
        <div className={styles.actions}>
          <Button variant="secondary" size="md" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={handleConfirm}
            disabled={del.isPending}
          >
            {del.isPending ? 'Deleting…' : 'Delete connection'}
          </Button>
        </div>
      </div>
    </SlidePanel>
  );
}
